import Phaser from 'phaser';
import {
  buildLookupContext,
  formatEvent,
  groupByTurn,
} from '../../game/battleLog.js';
import type {
  GameState,
  MapDefinition,
  PlayerId,
} from '../../game/types.js';
import { COLORS, FONT_HEADER, FONT_MONO, teamColorHex } from '../style.js';
import { Button } from './Button.js';

const BACKDROP_DEPTH = 195;
const PANEL_DEPTH = 200;
const CONTENT_DEPTH = 201;
const HEADER_DEPTH = 205;

const PANEL_INSET = 24;
const HEADER_HEIGHT = 72;
const FOOTER_GAP = 16;

const TURN_HEADER_GAP_TOP = 22;
const TURN_HEADER_GAP_BOTTOM = 8;
const ENTRY_LINE_HEIGHT = 22;
const SIDE_PADDING = 24;

export interface BattleLogModalOpts {
  readonly state: GameState;
  readonly map: MapDefinition;
  readonly designWidth: number;
  readonly designHeight: number;
  readonly onClose: () => void;
}

/**
 * Battle log modal. Full-canvas backdrop (catches pointer events),
 * centered panel, scrollable content showing every action in
 * reverse-chronological order grouped by turn. Built imperatively
 * inside GameScene so we don't have to plumb game state through a
 * parallel scene.
 *
 * Scrolling: drag-to-scroll on touch + mouse wheel on desktop, both
 * clamped so the user can't pull past the content edges. Same UX
 * pattern AboutScene uses.
 */
export class BattleLogModal {
  private readonly scene: Phaser.Scene;
  private readonly objects: Phaser.GameObjects.GameObject[] = [];
  private readonly buttons: Button[] = [];
  private readonly content: Phaser.GameObjects.Container;
  private contentHeight = 0;
  private viewportTop = 0;
  private viewportBottom = 0;
  private dragStart: { pointerY: number; contentY: number } | null = null;
  private readonly disposers: (() => void)[] = [];

  constructor(scene: Phaser.Scene, opts: BattleLogModalOpts) {
    this.scene = scene;

    // Full-canvas backdrop.
    const backdrop = scene.add.rectangle(
      opts.designWidth / 2,
      opts.designHeight / 2,
      opts.designWidth,
      opts.designHeight,
      0x000000,
      0.78
    );
    backdrop.setDepth(BACKDROP_DEPTH);
    backdrop.setInteractive();
    this.objects.push(backdrop);

    // Centered panel.
    const panelX = PANEL_INSET;
    const panelY = PANEL_INSET;
    const panelW = opts.designWidth - PANEL_INSET * 2;
    const panelH = opts.designHeight - PANEL_INSET * 2;
    const panel = scene.add.graphics();
    panel.fillStyle(0x040713, 1);
    panel.fillRoundedRect(panelX, panelY, panelW, panelH, 14);
    panel.lineStyle(2, COLORS.borderBrightInt, 0.9);
    panel.strokeRoundedRect(panelX, panelY, panelW, panelH, 14);
    panel.setDepth(PANEL_DEPTH);
    this.objects.push(panel);

    // Header band at top of panel.
    const headerBand = scene.add.graphics();
    headerBand.lineStyle(1, COLORS.borderSubtleInt, 0.7);
    headerBand.beginPath();
    headerBand.moveTo(panelX, panelY + HEADER_HEIGHT);
    headerBand.lineTo(panelX + panelW, panelY + HEADER_HEIGHT);
    headerBand.strokePath();
    headerBand.setDepth(HEADER_DEPTH);
    this.objects.push(headerBand);

    const title = scene.add
      .text(panelX + SIDE_PADDING, panelY + HEADER_HEIGHT / 2, 'BATTLE LOG', {
        fontFamily: FONT_HEADER,
        fontSize: '22px',
        color: COLORS.accentCyanHex,
        fontStyle: '700',
      })
      .setLetterSpacing(3)
      .setOrigin(0, 0.5)
      .setDepth(HEADER_DEPTH);
    this.objects.push(title);

    const closeBtn = new Button(scene, {
      x: panelX + panelW - SIDE_PADDING - 50,
      y: panelY + HEADER_HEIGHT / 2,
      width: 100,
      height: 44,
      text: 'Close',
      onTap: () => opts.onClose(),
    });
    closeBtn.setDepth(HEADER_DEPTH);
    this.buttons.push(closeBtn);

    // Scrollable content container — sits inside the panel below the
    // header band, masked so overflow disappears at the edges.
    this.viewportTop = panelY + HEADER_HEIGHT + 10;
    this.viewportBottom = panelY + panelH - FOOTER_GAP;

    this.content = scene.add.container(0, this.viewportTop);
    this.content.setDepth(CONTENT_DEPTH);
    this.objects.push(this.content);

    this.buildContent(opts);
    this.applyMask(panelX, panelW);
    this.installScrollInput(opts.designWidth, opts.designHeight);
  }

  // --- Content building ---

  private buildContent(opts: BattleLogModalOpts): void {
    const ctx = buildLookupContext(opts.state, opts.map);
    const reversed = [...opts.state.history].reverse();
    const groups = groupByTurn(reversed);

    let y = 0;
    if (groups.length === 0) {
      const empty = this.scene.add
        .text(SIDE_PADDING, y, 'No actions yet — start playing.', {
          fontFamily: FONT_MONO,
          fontSize: '14px',
          color: COLORS.textMuted,
        })
        .setOrigin(0, 0);
      this.content.add(empty);
      y += empty.height;
    } else {
      for (const group of groups) {
        // Turn header.
        const isSetup = group.turnNumber === 0;
        const headerLabel = isSetup
          ? '─── SETUP ───'
          : `─── TURN ${group.turnNumber} ───`;
        const header = this.scene.add
          .text(SIDE_PADDING, y + TURN_HEADER_GAP_TOP, headerLabel, {
            fontFamily: FONT_HEADER,
            fontSize: '15px',
            color: COLORS.textSecondary,
            fontStyle: '700',
          })
          .setLetterSpacing(2)
          .setOrigin(0, 0);
        this.content.add(header);
        y +=
          TURN_HEADER_GAP_TOP +
          header.height +
          TURN_HEADER_GAP_BOTTOM;

        // Entries within the turn — keep newest at top of group too.
        for (const entry of group.entries) {
          const actorColor = this.actorColorFor(opts.state, entry.actor);
          for (const ev of entry.events) {
            const line = formatEvent(ev, ctx);
            const text = this.scene.add
              .text(SIDE_PADDING + 6, y, line, {
                fontFamily: FONT_MONO,
                fontSize: '13px',
                color: actorColor,
              })
              .setOrigin(0, 0);
            this.content.add(text);
            y += ENTRY_LINE_HEIGHT;
          }
        }
      }
    }
    this.contentHeight = y;
  }

  /**
   * Color a log line with the acting player's team color in team
   * play, or the player color in FFA. System events (no actor)
   * fall back to muted text.
   */
  private actorColorFor(
    state: GameState,
    actorId: PlayerId | null
  ): string {
    if (!actorId) return COLORS.textMuted;
    const player = state.players.find((p) => p.id === actorId);
    if (!player) return COLORS.textMuted;
    const teams = new Set(state.players.map((p) => p.team));
    const isFFA = teams.size === state.players.length;
    return isFFA ? player.color : teamColorHex(player.team);
  }

  // --- Scroll input + masking ---

  private applyMask(panelX: number, panelW: number): void {
    const shape = this.scene.make.graphics({});
    shape.fillStyle(0xffffff, 1);
    shape.fillRect(
      panelX,
      this.viewportTop,
      panelW,
      this.viewportBottom - this.viewportTop
    );
    const mask = shape.createGeometryMask();
    this.content.setMask(mask);
  }

  private installScrollInput(designWidth: number, designHeight: number): void {
    const onPointerDown = (p: Phaser.Input.Pointer): void => {
      // Only initiate drag if the pointer is in the content area.
      if (
        p.y < this.viewportTop ||
        p.y > this.viewportBottom ||
        p.x < PANEL_INSET ||
        p.x > designWidth - PANEL_INSET
      ) {
        return;
      }
      this.dragStart = { pointerY: p.y, contentY: this.content.y };
    };
    const onPointerMove = (p: Phaser.Input.Pointer): void => {
      if (!p.isDown || !this.dragStart) return;
      const dy = p.y - this.dragStart.pointerY;
      this.content.y = this.clampScroll(this.dragStart.contentY + dy);
    };
    const onPointerUp = (): void => {
      this.dragStart = null;
    };
    const onWheel = (
      _p: Phaser.Input.Pointer,
      _go: Phaser.GameObjects.GameObject[],
      _dx: number,
      deltaY: number
    ): void => {
      this.content.y = this.clampScroll(this.content.y - deltaY);
    };

    this.scene.input.on('pointerdown', onPointerDown);
    this.scene.input.on('pointermove', onPointerMove);
    this.scene.input.on('pointerup', onPointerUp);
    this.scene.input.on('pointerupoutside', onPointerUp);
    this.scene.input.on('wheel', onWheel);

    // Save off so destroy() can detach.
    this.disposers.push(() => {
      this.scene.input.off('pointerdown', onPointerDown);
      this.scene.input.off('pointermove', onPointerMove);
      this.scene.input.off('pointerup', onPointerUp);
      this.scene.input.off('pointerupoutside', onPointerUp);
      this.scene.input.off('wheel', onWheel);
    });

    // Silence unused-var warning when designHeight isn't needed.
    void designHeight;
  }

  private clampScroll(rawY: number): number {
    const minY = Math.min(
      this.viewportTop,
      this.viewportBottom - this.contentHeight
    );
    const maxY = this.viewportTop;
    if (rawY > maxY) return maxY;
    if (rawY < minY) return minY;
    return rawY;
  }

  destroy(): void {
    for (const d of this.disposers) d();
    for (const b of this.buttons) b.destroy();
    for (const o of this.objects) o.destroy();
  }
}
